import { Spacing } from '@persona-id/design-tokens';
import { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import OutputsList from 'dashboard/components/WorkflowControlPanel/OutputConfig/OutputsList';
import ResultOutput from 'dashboard/components/WorkflowControlPanel/OutputConfig/ResultOutput';
import StepConfigTypeCard from 'dashboard/components/WorkflowControlPanel/StepConfigTypeCard';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import CodeHighlight from 'dashboard/components-lib/CodeHighlight';
import { generateJsonSchemaConfig } from 'dashboard/lib/workflows';
import { TransactionType } from 'dashboard/models/transaction-type';
import { Trigger } from 'dashboard/models/trigger';
import { WorkflowError } from 'dashboard/models/workflow';
import {
  OUTPUT_STEP_TOKEN,
  WorkflowStepType,
  WorkflowVersionOutput,
} from 'dashboard/models/workflow-version';
import { getUsableObjects } from 'dashboard/redux/selectors/workflow-editor-context';
import { RootState } from 'dashboard/redux/store';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { headingMixins } from 'design-system/mixins/text';
import { ApiModel } from 'lib/interfaces/api';

const StyledCodeHighlight = styled(CodeHighlight)`
  border: 1px solid ${({ theme }) => theme.color.border.input};
  border-radius: ${({ theme }) => theme.element.borderRadius};
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const SubTitle = styled.div`
  ${headingMixins.sm}
  padding-bottom: ${Spacing[2]};
`;

interface OutputConfigProps {
  trigger?: Trigger;
  editable: boolean;
  outputs: WorkflowVersionOutput[];
  runOutput: Record<string, any> | null;
  errors: WorkflowError[];
  transactionTypes: TransactionType[];
  updateOutput: (outputs: WorkflowVersionOutput[]) => void;
  handleClose: () => void;
}

const OutputConfig: FC<OutputConfigProps> = ({
  trigger,
  editable,
  outputs,
  transactionTypes = [],
  runOutput,
  errors,
  updateOutput,
  handleClose,
}) => {
  const { renderErrorMessageFor } = useWorkflowError(errors);
  const isComputedFieldTrigger = trigger?.type === ApiModel.TriggerComputedField;
  const usableObjects = useSelector((state: RootState) =>
    getUsableObjects(state, OUTPUT_STEP_TOKEN),
  );
  const { jsonSchema, tokensToDisplayValue } = useMemo(
    () =>
      generateJsonSchemaConfig({
        usableObjects,
      }),
    [usableObjects],
  );

  return (
    <>
      <Container>
        <SubTitle>Output Step</SubTitle>
        <Spacer height={2} />
        <StepConfigTypeCard type={WorkflowStepType.Output} />
        <Spacer height={2} />
        <SubTitle>Configuration</SubTitle>
        {isComputedFieldTrigger ? (
          <>
            <ResultOutput
              editable={editable}
              jsonSchema={jsonSchema}
              outputs={outputs}
              tokensToDisplayValue={tokensToDisplayValue}
              transactionTypes={transactionTypes}
              trigger={trigger}
              updateOutput={updateOutput}
            />
            {editable && renderErrorMessageFor('outputs')}
          </>
        ) : (
          <OutputsList
            editable={editable}
            errors={errors}
            outputs={outputs}
            usableObjects={usableObjects}
          />
        )}
        {runOutput && (
          <>
            <Spacer height={2} />
            <SubTitle>Output result</SubTitle>
            <Spacer height={2} />
            <StyledCodeHighlight language='json' light>
              {JSON.stringify(runOutput, null, 2)}
            </StyledCodeHighlight>
          </>
        )}
      </Container>
      <Spacer height={3} />
      <Button id='workflow-output-step-control-panel__close-button' onClick={handleClose}>
        Close
      </Button>
    </>
  );
};

export default OutputConfig;
